import { Info } from '@mui/icons-material';
import { Box, TextField, Typography, useTheme } from '@mui/material';
import { getBrandLang } from '@prismamedia/one-brandkey';
import {
  EditAppBar,
  NonEditableField,
  NotificationTypeEnum,
  OneMedia,
  PageWithDrawer,
  SimpleSelect,
  useAdvice,
  useNotification,
} from '@prismamedia/one-components';
import { ContentState, convertToRaw, RawDraftContentState } from 'draft-js';
import { FC, SetStateAction, useCallback, useRef, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { BrandKey, TagLang, TagType } from '../../__generated__/queries-web';
import { Draft } from '../../components/Draft';
import { actions as DraftActions } from '../../components/Draft/DraftActions';
import { MediaInput } from '../../components/MediaInput';
import { MEDIA_TYPES } from '../../components/MediaInput/constants';
import { paths } from '../../routing/Router/paths';
import { AdvicePopper } from '../ArticleEdit/ArticleEditor/AdvicePopper';
import { allowedTagTypes, tagLangLabelByValue } from '../TagList/utils';
import { toCreateTagInput, useCreateTag } from './tagCreate.web.graphql';
import {
  hasBodyContent,
  isAllowedTagLang,
  isAllowedTagType,
  isTitleValid,
} from './utils';

export const TagCreate: FC = () => {
  const history = useHistory();
  const theme = useTheme();
  const { pushNotification } = useNotification();
  const { openAdvice } = useAdvice();
  const textFieldRef = useRef(null);
  const { brandKey } = useParams<{ brandKey: BrandKey }>();
  const [id] = useState(() => crypto.randomUUID());
  const [title, setTitle] = useState('');
  const [type, setType] = useState(TagType.Tag);
  const [lang, setLang] = useState<TagLang>(() => {
    const brandLang = getBrandLang(brandKey);
    return isAllowedTagLang(brandLang) ? brandLang : TagLang.FR;
  });
  const [media, setMediaState] = useState<OneMedia | null>(null);
  const [body, setBody] = useState<RawDraftContentState>(() =>
    convertToRaw(ContentState.createFromText('')),
  );
  const [saveLoading, setSaveLoading] = useState(false);
  const [createTag] = useCreateTag();
  const canSave = isTitleValid(title);
  const showTitleError = title.trim().length > 0 && !canSave;

  const setMedia = (setter: SetStateAction<OneMedia[]>) => {
    const nextMedia =
      typeof setter === 'function' ? setter(media ? [media] : []) : setter;
    setMediaState(nextMedia[0] || null);
  };

  const onSave = async () => {
    if (!canSave) return;

    setSaveLoading(true);
    try {
      await createTag({
        variables: {
          data: toCreateTagInput({
            id,
            title,
            type,
            lang,
            brandKey,
            body: hasBodyContent(body) ? body : undefined,
            media: media || undefined,
          }),
        },
      });
      pushNotification({
        type: NotificationTypeEnum.success,
        message: 'Tag créé',
      });
      history.push(generatePath(paths.TAG_EDIT, { id, brandKey }));
    } catch (error) {
      pushNotification({
        message:
          error instanceof Error
            ? error.message
            : 'Erreur durant la création du tag',
        type: NotificationTypeEnum.error,
      });
      setSaveLoading(false);
    }
  };

  const handleInfoIconClick = useCallback(
    (anchorRef, content) => () => {
      openAdvice({
        anchorRef,
        content,
        PopperProps: {
          keepMounted: false,
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundariesElement: 'window',
                enabled: false,
                escapeWithReference: true,
              },
            },
          ],
          style: { width: '310px' },
        },
        zIndex: 1,
      });
    },
    [openAdvice],
  );

  return (
    <>
      <EditAppBar
        title={title.trim() || 'Nouveau tag'}
        onNavigateBack={() => history.push(paths.TAG_LIST)}
        save={{
          disabled: !canSave,
          loading: saveLoading,
          onClick: onSave,
        }}
      />
      <PageWithDrawer
        rightDrawer={
          <>
            <NonEditableField label="Id du tag" value={id} sx={{ mb: 2 }} />
            <NonEditableField label="Marque" value={brandKey} sx={{ mb: 2 }} />
            <NonEditableField
              label="Statut"
              value={
                <Typography sx={{ color: theme.palette.error.main }}>
                  Inactif
                </Typography>
              }
              sx={{ mb: 2 }}
            />
            <NonEditableField
              label="Nombre d'articles liés"
              value={0}
              sx={{ mb: 2 }}
            />
            <SimpleSelect
              label="Type"
              sx={{ mb: 2, width: '100%' }}
              options={allowedTagTypes.map((tagType) => ({
                value: tagType,
                label: tagType,
              }))}
              value={type}
              onChange={(_, value) => {
                if (isAllowedTagType(value)) setType(value);
              }}
            />
            <SimpleSelect
              label="Langue"
              sx={{ mb: 2, width: '100%' }}
              options={Object.entries(
                tagLangLabelByValue,
              ).map(([value, label]) => ({ value, label }))}
              value={lang}
              onChange={(_, value) => {
                if (isAllowedTagLang(value)) setLang(value);
              }}
            />
          </>
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Info sx={{ fontSize: 18, mr: 1, color: 'info.main' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Le tag doit être lié à au moins un article pour apparaître dans la
            liste.
          </Typography>
        </Box>
        <TextField
          label="Intitulé"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          required
          fullWidth
          inputProps={{ maxLength: 255 }}
          error={showTitleError}
          helperText={
            showTitleError ? 'Au moins 2 caractères alphanumériques' : undefined
          }
          sx={{ mb: 4 }}
        />
        <MediaInput
          medias={media ? [media] : []}
          setMedias={setMedia}
          brandKey={brandKey}
          allowedMedias={[MEDIA_TYPES.IMAGE]}
          sx={{ mb: 4 }}
        />
        <Draft
          rawDraft={body}
          onChange={(value) => {
            if (value) setBody(value);
          }}
          actions={[
            DraftActions.BOLD,
            DraftActions.ITALIC,
            DraftActions.LINK,
            DraftActions.TITLE,
          ]}
          placeholder={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box>Texte</Box>
              <Info
                sx={{ marginLeft: '0.5rem', color: 'black' }}
                onClick={handleInfoIconClick(
                  textFieldRef,
                  <AdvicePopper
                    accordions={[
                      {
                        details: `Les pages de tags permettent de regrouper les articles traitant d'une même thématique.\nLe texte d'introduction de chaque page tag doit présenter cette thématique et encourager les internautes à explorer les articles associés.\nLe contenu doit rester sous la limite de 300 caractères, en étant concis et engageant pour le lecteur.`,
                        summary: 'Texte inférieur à 300 caractères',
                      },
                    ]}
                    title="Conseil de saisie"
                  />,
                )}
                ref={textFieldRef}
              />
            </Box>
          }
        />
      </PageWithDrawer>
    </>
  );
};
